/**
 * "Oct 18" — the year only when it is not the current one (Figma annotation
 * `3609:69226`). Invalid or missing dates give an empty string.
 */
export function formatPriceDate(iso: string | null | undefined, locale: string, now = new Date()): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const sameYear = date.getFullYear() === now.getFullYear()
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
}

/** Same calendar day as `now` (or a clock skew ahead of us) — "updated today". */
export function isPriceDateToday(iso: string | null | undefined, now = new Date()): boolean {
  if (!iso) return false
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return false
  const startOfDay = (value: Date) => new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime()
  return startOfDay(date) >= startOfDay(now)
}

/** "13:30" — 24-hour clock in every locale (Figma `3609:69145` history note). Empty when unknown. */
export function formatPriceTime(iso: string | null | undefined, locale: string): string {
  if (!iso || !/T\d{2}:\d{2}/.test(iso)) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
}

/** The local calendar day of an ISO timestamp as `YYYY-MM-DD`; empty when unknown. */
function localDayKey(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * Stale price — the history chart ends where the price was last seen. The API carries the
 * chart forward to today; points dated after `updatedAt` are folded into one point ON the
 * update day, holding the latest known price (the last point's value). Points are compared by
 * their `YYYY-MM-DD` prefix; a chart whose dates are not ISO-like is left as it is. Pure.
 */
export function trimPriceHistoryToUpdate<T extends { x: string; y: number }>(
  points: readonly T[],
  updatedAt: string | null | undefined,
): T[] {
  if (!updatedAt) return [...points]
  const until = localDayKey(updatedAt)
  if (!until) return [...points]
  const iso = /^\d{4}-\d{2}-\d{2}/
  if (!points.every((point) => iso.test(point.x))) return [...points]
  const kept = points.filter((point) => point.x.slice(0, 10) <= until)
  const last = points[points.length - 1]
  if (kept.length === points.length || !last) return kept
  if (kept[kept.length - 1]?.x.slice(0, 10) === until) return kept
  return [...kept, { ...last, x: until }]
}
